/* eslint-disable react/no-children-prop */
import React, { useCallback } from "react";
import { DateTime } from "luxon";
import { getRouteApi } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
// form
import { useAppForm } from "@components/form/app-form";
// UI
import { IconEdit, IconX, IconCheck } from "@tabler/icons-react";
import {
  Container,
  Tooltip,
  Pill,
  Flex,
  ThemeIcon,
  Group,
  ActionIcon,
  Divider,
  Card,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { OnlyShowForStaff } from "@components/only-show-for-staff";
import { TanstackMantineLink } from "@components/tanstack-maintine-link";
// API
import { useSuspenseQuery, useQuery } from "@tanstack/react-query";
import {
  retrieveAccountQueryOpts,
  useSetAccountPlayerLimitMutation,
} from "@src/services/apiQueries/account-queries";
import { retrieveUserQueryOpts } from "@src/services/apiQueries/userQueries";

const route = getRouteApi("/_authenticated/app/accounts/$accountId");

function StatBlock(
  props: React.PropsWithChildren<{
    title: string;
  }>,
): React.ReactNode {
  return (
    <Card shadow="sm" padding="sm" radius="md" withBorder>
      <Text fw="bold">{props.title}</Text>
      {props.children}
    </Card>
  );
}

interface UpdatePlayerLimitProps {
  currPlayerLimit: number | null;
  onSubmit: (value: number | null) => Promise<void>;
}

function UpdatePlayerLimit(props: UpdatePlayerLimitProps): React.ReactNode {
  const { t } = useTranslation();
  const [isEditable, { close: lockInput, open: unlockInput }] =
    useDisclosure(false);

  const form = useAppForm({
    defaultValues: {
      playerLimit: props.currPlayerLimit,
    },
    onSubmit: async ({ value }) => {
      console.log("submit", value);

      const { playerLimit } = value;
      const newPlayerLimit =
        playerLimit === null || isNaN(playerLimit) ? null : playerLimit;

      await props.onSubmit(newPlayerLimit);
      lockInput();
    },
  });

  const handleClickCancel = useCallback(() => {
    form.reset();
    lockInput();
  }, [form, lockInput]);

  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault();
        e.stopPropagation();
        await form.handleSubmit();
      }}
    >
      <form.AppField
        name="playerLimit"
        children={(field) => {
          return (
            <field.NumberField
              label={t("accountDetails.playerLimit")}
              // if null, empty string is what makes placeholder value show up
              value={field.state.value ?? ""}
              placeholder={t("utils.notSet")}
              min={1}
              onChange={(v) =>
                field.handleChange(typeof v === "string" ? parseInt(v) : v)
              }
              disabled={!isEditable}
              inputContainer={(children) => {
                return (
                  <Group>
                    {children}

                    {isEditable ? (
                      <>
                        <Tooltip label={t("accountDetails.cancel")}>
                          <ThemeIcon
                            variant="light"
                            color="red"
                            onClick={handleClickCancel}
                          >
                            <IconX />
                          </ThemeIcon>
                        </Tooltip>

                        <Tooltip label={t("accountDetails.submit")}>
                          <ActionIcon
                            variant="light"
                            disabled={field.state.meta.isDefaultValue}
                            color="green"
                            loading={form.state.isSubmitting}
                            type="submit"
                          >
                            <IconCheck />
                          </ActionIcon>
                        </Tooltip>
                      </>
                    ) : (
                      <ThemeIcon
                        variant="light"
                        color="blue"
                        onClick={unlockInput}
                      >
                        <IconEdit />
                      </ThemeIcon>
                    )}
                  </Group>
                );
              }}
            />
          );
        }}
      />
    </form>
  );
}

export function AccountDetailsPage(): React.ReactNode {
  const { t } = useTranslation();
  const { parsedAccountId } = route.useLoaderData();

  const { data: account } = useSuspenseQuery(
    retrieveAccountQueryOpts(parsedAccountId),
  );

  const { data: primaryUser } = useQuery(
    retrieveUserQueryOpts(account.primary_contact),
  );

  const { mutateAsync } = useSetAccountPlayerLimitMutation();

  const handleChangePlayerLimit = useCallback(
    async (newPlayerLimit: number | null) => {
      await mutateAsync({ accountId: account.id, playerLimit: newPlayerLimit });
    },
    [account.id, mutateAsync],
  );

  return (
    <Container>
      <Stack>
        <Title order={2}>{t("accountDetails.overview")}</Title>
        <Flex gap="md">
          <OnlyShowForStaff>
            <StatBlock title={t("accountDetails.accountType")}>
              <Text>{account.account_type}</Text>
            </StatBlock>
          </OnlyShowForStaff>
          <StatBlock title={t("accountDetails.dateJoined")}>
            {primaryUser ? (
              <Text>
                {DateTime.fromISO(primaryUser.date_joined).toLocaleString()}
              </Text>
            ) : null}
          </StatBlock>

          <OnlyShowForStaff>
            <StatBlock title={t("accountDetails.tags")}>
              <Flex gap="xs">
                {account.tags.map((tg, idx) => {
                  return <Pill key={idx}>{tg}</Pill>;
                })}
              </Flex>
            </StatBlock>
          </OnlyShowForStaff>
        </Flex>

        <TanstackMantineLink
          to="/app/accounts/$accountId/players"
          params={{ accountId: account.id.toString() }}
        >
          {t("accountDetails.viewPlayers")}
        </TanstackMantineLink>
        <Divider />

        <OnlyShowForStaff>
          <Title order={2}>{t("accountDetails.editAccount")}</Title>
          <UpdatePlayerLimit
            onSubmit={handleChangePlayerLimit}
            currPlayerLimit={account.player_limit}
          />
        </OnlyShowForStaff>
      </Stack>
    </Container>
  );
}
